/**
 * EmptyStateIllustration - Isometric 3D empty state visuals for Stock Sentry
 *
 * Design Philosophy: "Friendly Isometric"
 * - Balanced, polished isometric 3D style (Shopify-inspired)
 * - RetailForge brand purple palette (#8b7bd8 and variations)
 * - Object-focused, no characters
 * - Friendly and approachable mood
 *
 * SVG assets in public/illustrations/isometric/ for maintainability.
 * @see TECHNICAL_DEBT_ROADMAP.md Phase 2.3
 */

export type EmptyStateVariant =
  | 'default'
  | 'dashboard-header'
  | 'no-products'
  | 'no-alerts'
  | 'no-active-alerts'
  | 'no-monitored-products'
  | 'no-alert-history'
  | 'no-product-alerts'
  | 'no-notifications'
  | 'no-emails'
  | 'no-channels'
  | 'weekly-digest'
  | 'notification-emails'
  | 'email-recipients'
  | 'slack'
  | 'webhooks'
  | 'data-export';

interface EmptyStateIllustrationProps {
  /** The type of empty state to display */
  variant?: EmptyStateVariant;
  /** Width of the illustration (default: 160) */
  width?: number;
  /** Height of the illustration (default: 120) */
  height?: number;
  /** Optional className for additional styling */
  className?: string;
  /** Margin for centering the illustration */
  margin?: string;
}

/**
 * Maps variant names to their asset paths
 */
const ILLUSTRATION_PATHS: Record<EmptyStateVariant, string> = {
  'default': '/illustrations/isometric/default.svg',
  'dashboard-header': '/illustrations/isometric/dashboard-header.svg',
  'no-products': '/illustrations/isometric/no-products.svg',
  'no-alerts': '/illustrations/isometric/no-alerts.svg',
  'no-active-alerts': '/illustrations/isometric/no-active-alerts.svg',
  'no-monitored-products': '/illustrations/isometric/no-monitored-products.svg',
  'no-alert-history': '/illustrations/isometric/no-alert-history.svg',
  'no-product-alerts': '/illustrations/isometric/no-product-alerts.svg',
  'no-notifications': '/illustrations/isometric/no-notifications.svg',
  'no-emails': '/illustrations/isometric/no-emails.svg',
  'no-channels': '/illustrations/isometric/no-channels.svg',
  'weekly-digest': '/illustrations/isometric/weekly-digest.svg',
  'notification-emails': '/illustrations/isometric/notification-emails.svg',
  'email-recipients': '/illustrations/isometric/email-recipients.svg',
  'slack': '/images/empty-states/slack-empty-state.png',
  'webhooks': '/images/empty-states/webhook-empty-state.png',
  'data-export': '/illustrations/isometric/data-export.svg',
};

export function EmptyStateIllustration({
  variant = 'default',
  width = 160,
  height = 120,
  margin = '0 auto',
  className,
}: EmptyStateIllustrationProps) {
  const src = ILLUSTRATION_PATHS[variant];

  return (
    <div
      className="empty-state-image-wrapper"
      style={{ width, height, margin }}
    >
      <img
        src={src}
        alt={`Empty state illustration: ${variant}`}
        width={width}
        height={height}
        className={`empty-state-image ${className || ''}`}
        style={{ display: 'block', objectFit: 'contain' }}
      />
    </div>
  );
}

export default EmptyStateIllustration;
